/**
 * @file Section de saisie d'un bon cadeau dans le widget de réservation.
 * @module packages.common.components.table-reservation.components.GiftVoucherSection
 *
 * Permet au client d'indiquer qu'il dispose d'un bon cadeau et de saisir son
 * numéro. Le widget n'effectue aucune vérification (texte libre) ; le statut
 * et le solde sont restitués côté backoffice via un lookup authentifié.
 */

import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';

// icons
import Iconify from 'packages/common/components/iconify';
import {clientInputStyles} from '../styles/ReservationFlowStyles';

// ----------------------------------------------------------------------

// Champ texte du numéro de bon cadeau
const GiftVoucherCodeInput = styled(OutlinedInput)({
  cursor: 'text',
  width: '100%',
  ...clientInputStyles,
});

// ----------------------------------------------------------------------

/**
 * Section "bon cadeau" du widget de réservation client.
 *
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.formData - Données du formulaire (lecture)
 * @param {boolean} props.formData.hasGiftVoucher - Le client déclare avoir un bon cadeau
 * @param {string} props.formData.giftVoucherCode - Numéro du bon cadeau saisi
 * @param {Function} props.updateFormData - Setter du formulaire (champ, valeur)
 * @param {Function} props.handleInputClick - Stoppe la propagation des clics
 * @param {Function} [props.t] - Fonction de traduction i18next
 * @return {JSX.Element}
 */
export function GiftVoucherSection({formData, updateFormData, handleInputClick, t}) {
  const handleToggleChange = (event) => {
    event.stopPropagation();
    const hasVoucher = event.target.value === 'yes';
    updateFormData('hasGiftVoucher', hasVoucher);
    if (!hasVoucher) {
      updateFormData('giftVoucherCode', '');
    }
  };

  const handleCodeChange = (event) => {
    event.stopPropagation();
    updateFormData('giftVoucherCode', event.target.value);
  };

  return (
    <Box sx={{mb: 3}}>
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
          <Iconify icon="mdi:gift-outline" sx={{fontSize: 20, color: 'primary.main'}} aria-hidden="true" />
          <FormLabel
            component="legend"
            id="gift-voucher-question-label"
            sx={{typography: 'body2', fontWeight: 600, color: 'text.primary'}}
          >
            {t ?
              t('widgets.reservation.form.giftVoucher.question') :
              'Disposez-vous d\'un bon cadeau ?'}
          </FormLabel>
        </Box>

        <RadioGroup
          value={formData.hasGiftVoucher ? 'yes' : 'no'}
          onChange={handleToggleChange}
          aria-labelledby="gift-voucher-question-label"
          sx={{
            flexDirection: 'row',
            gap: 2,
            mb: formData.hasGiftVoucher ? 2 : 0,
          }}
        >
          <FormControlLabel
            value="no"
            control={<Radio size="small" />}
            label={t ? t('widgets.reservation.form.giftVoucher.no') : 'Non'}
          />
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label={t ? t('widgets.reservation.form.giftVoucher.yes') : 'Oui'}
          />
        </RadioGroup>

        {formData.hasGiftVoucher && (
          <Fade in timeout={300}>
            <Box sx={{mt: 1}} onClick={(e) => e.stopPropagation()}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="gift-voucher-code">
                  {t ?
                    t('widgets.reservation.form.giftVoucher.codeLabel') :
                    'Numéro du bon cadeau'}
                </InputLabel>
                <GiftVoucherCodeInput
                  id="gift-voucher-code"
                  label={t ?
                    t('widgets.reservation.form.giftVoucher.codeLabel') :
                    'Numéro du bon cadeau'}
                  value={formData.giftVoucherCode || ''}
                  onChange={handleCodeChange}
                  onClick={handleInputClick}
                  onFocus={handleInputClick}
                  onMouseDown={handleInputClick}
                  onMouseUp={handleInputClick}
                  placeholder={t ?
                    t('widgets.reservation.form.giftVoucher.codePlaceholder') :
                    'Ex: HOTEL-K3HQ7R'}
                  inputProps={{maxLength: 50}}
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify icon="mdi:gift-outline" fontSize="small" color="primary" aria-hidden="true" />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
}

GiftVoucherSection.propTypes = {
  formData: PropTypes.shape({
    hasGiftVoucher: PropTypes.bool,
    giftVoucherCode: PropTypes.string,
  }).isRequired,
  updateFormData: PropTypes.func.isRequired,
  handleInputClick: PropTypes.func.isRequired,
  t: PropTypes.func,
};
